/* platform/AuditLogs — Platform-wide compliance audit trail.
   Live API fetch, server-side pagination + filters, date range, CSV/Excel export. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { list as listAudit } from '../api/audit.api.js';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { exportCSV, FilterChip } from '../shared/data-table';
import { exportExcel } from './utils/exportUtils.js';
import { StatCard, PageHead, Th } from './_ui';

const PAGE_SIZE = 100;
const SEV_TONE = { info: 'blue', warning: 'amber', error: 'red', critical: 'red' };
const CATS = ['Auth', 'Backup', 'License', 'Member', 'Org', 'Partner', 'Role', 'Security', 'Settings', 'Subscription', 'System', 'Workspace'];
const EXPORT_COLS = ['who', 'action', 'target', 'cat', 'sev', 'time'];
const SKELETON_WIDTHS = [
  { key: 'actor',    w: 80  },
  { key: 'action',   w: 180 },
  { key: 'target',   w: 140 },
  { key: 'category', w: 80  },
  { key: 'severity', w: 60  },
  { key: 'when',     w: 70  },
];

export default function AuditLogs() {
  const toast = useToast();
  const [q, setQ]           = useState('');
  const [cat, setCat]       = useState(null);
  const [sev, setSev]       = useState(null);
  const [from, setFrom]     = useState('');
  const [to, setTo]         = useState('');
  const [rows, setRows]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [exporting, setExporting] = useState(false);

  const hasMore = rows.length < total;

  // Debounce search input — only emit after 350 ms idle
  const [debouncedQ, setDebouncedQ] = useState('');
  const debounceRef = useRef(null);
  const handleQ = (val) => {
    setQ(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(val), 350);
  };

  // Reset + fetch page 1 whenever filters change
  useEffect(() => {
    let cancelled = false;
    setRows([]);
    setTotal(0);
    setPage(1);
    setLoading(true);
    setError(null);
    listAudit({
      q: debouncedQ || undefined,
      cat: cat || undefined,
      sev: sev || undefined,
      from: from || undefined,
      to: to || undefined,
      page: 1,
      limit: PAGE_SIZE,
    })
      .then(res => {
        if (!cancelled) {
          setRows(res.data ?? []);
          setTotal(res.total ?? 0);
        }
      })
      .catch(() => { if (!cancelled) setError('Failed to load audit logs.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQ, cat, sev, from, to]);

  const loadMore = useCallback(async () => {
    const nextPage = page + 1;
    setLoading(true);
    try {
      const res = await listAudit({
        q: debouncedQ || undefined,
        cat: cat || undefined,
        sev: sev || undefined,
        from: from || undefined,
        to: to || undefined,
        page: nextPage,
        limit: PAGE_SIZE,
      });
      setRows(prev => [...prev, ...(res.data ?? [])]);
      setPage(nextPage);
    } catch {
      setError('Failed to load more events.');
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, cat, sev, from, to, page]);

  const refresh = useCallback(() => {
    setDebouncedQ(q);
  }, [q]);

  const handleExportCSV = useCallback(async () => {
    setExporting(true);
    try {
      const res = await listAudit({
        q: debouncedQ || undefined,
        cat: cat || undefined,
        sev: sev || undefined,
        from: from || undefined,
        to: to || undefined,
        page: 1,
        limit: 5000,
      });
      const exportRows = (res.data ?? []).map(({ id, ...r }) => r);
      exportCSV('platform-audit-logs', exportRows, EXPORT_COLS);
      toast({ tone: 'success', icon: 'Download', title: `Exported ${exportRows.length} events` });
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Export failed' });
    } finally {
      setExporting(false);
    }
  }, [debouncedQ, cat, sev, from, to, toast]);

  const handleExportExcel = useCallback(async () => {
    setExporting(true);
    try {
      const res = await listAudit({
        q: debouncedQ || undefined,
        cat: cat || undefined,
        sev: sev || undefined,
        from: from || undefined,
        to: to || undefined,
        page: 1,
        limit: 5000,
      });
      const exportRows = (res.data ?? []).map(({ id, ...r }) => r);
      exportExcel('platform-audit-logs', [{ name: 'Audit Logs', rows: exportRows, columns: EXPORT_COLS }]);
      toast({ tone: 'success', icon: 'FileSpreadsheet', title: `Exported ${exportRows.length} events` });
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Export failed' });
    } finally {
      setExporting(false);
    }
  }, [debouncedQ, cat, sev, from, to, toast]);

  const clearFilters = () => {
    setCat(null);
    setSev(null);
    setFrom('');
    setTo('');
    setQ('');
    clearTimeout(debounceRef.current);
    setDebouncedQ('');
  };

  const hasFilters = cat || sev || debouncedQ || from || to;
  const warnings  = rows.filter(a => a.sev === 'warning').length;
  const security  = rows.filter(a => a.cat === 'Security').length;

  function renderTableBody() {
    if (loading && rows.length === 0) {
      return Array.from({ length: 8 }, (_, i) => (
        <tr key={`skeleton-row-${i}`} className="nx-tr">
          {SKELETON_WIDTHS.map(({ key, w }) => (
            <td key={key}><div className="nx-cc-skeleton" style={{ height: 14, width: w, borderRadius: 4 }} /></td>
          ))}
        </tr>
      ));
    }
    if (rows.length === 0) {
      const emptyMsg = hasFilters ? 'No events match the current filters.' : 'No audit events yet.';
      return (
        <tr>
          <td colSpan={6}>
            <div className="nx-empty" style={{ padding: '32px 0' }}>
              <NexusIcon name="ClipboardList" size={20} />
              <span>{emptyMsg}</span>
            </div>
          </td>
        </tr>
      );
    }
    return rows.map(a => (
      <tr key={a.id} className="nx-tr">
        <td>
          <span className="nx-row-name">
            <span className="nx-domain-mark" style={{ width: 22, height: 22, fontSize: 10 }}>{(a.who || 'S')[0].toUpperCase()}</span>
            <span className="nx-row-name-text">{a.who || '—'}</span>
          </span>
        </td>
        <td><span className="nx-row-name-text">{a.action}</span></td>
        <td><span className="nx-muted">{a.target || '—'}</span></td>
        <td><Pill tone="slate">{a.cat}</Pill></td>
        <td><Pill tone={SEV_TONE[a.sev] || 'gray'}>{a.sev || 'info'}</Pill></td>
        <td><span className="nx-muted nx-mini">{a.time}</span></td>
      </tr>
    ));
  }

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Audit Logs"
        sub={total > 0 ? `${total.toLocaleString()} total events` : 'No events'}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" icon="RefreshCw" onClick={refresh} disabled={loading}>Refresh</Btn>
            <Btn variant="ghost" icon="Download" onClick={handleExportCSV} disabled={exporting || loading}>CSV</Btn>
            <Btn variant="ghost" icon="FileSpreadsheet" onClick={handleExportExcel} disabled={exporting || loading}>Excel</Btn>
          </div>
        }
      />

      <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <StatCard label="Total Events"    value={total.toLocaleString()} icon="ClipboardList" color="#125AB0" />
        <StatCard label="Warnings"        value={warnings} sub="in loaded view" icon="AlertCircle" color="#E08A00" />
        <StatCard label="Security Events" value={security} sub="in loaded view" icon="Shield" color="#DE3217" />
        <StatCard label="Showing"         value={rows.length} sub={`of ${total.toLocaleString()}`} icon="Layers" color="#6E3FBA" />
      </div>

      <div className="nx-toolbar-filters" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '14px 0', flexWrap: 'wrap' }}>
        <div className="nx-search-inline" style={{ width: 280 }}>
          <NexusIcon name="Search" size={13} />
          <input value={q} onChange={e => handleQ(e.target.value)} placeholder="Search actor, action, target…" />
        </div>
        <FilterChip icon="Layers"    label="Category" value={cat} options={CATS}                             onChange={setCat} active={!!cat} />
        <FilterChip icon="Activity"  label="Severity" value={sev} options={['info', 'warning', 'error', 'critical']} onChange={setSev} active={!!sev} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <NexusIcon name="Calendar" size={13} style={{ color: 'var(--nx-text-muted)' }} />
          <input
            type="date"
            value={from}
            onChange={e => setFrom(e.target.value)}
            style={{ fontSize: 12, padding: '4px 6px', border: '1px solid var(--nx-border)', borderRadius: 6, background: 'var(--nx-surface)', color: 'var(--nx-text)', height: 30 }}
            title="From date"
          />
          <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>→</span>
          <input
            type="date"
            value={to}
            onChange={e => setTo(e.target.value)}
            min={from || undefined}
            style={{ fontSize: 12, padding: '4px 6px', border: '1px solid var(--nx-border)', borderRadius: 6, background: 'var(--nx-surface)', color: 'var(--nx-text)', height: 30 }}
            title="To date"
          />
        </div>
        {hasFilters && (
          <Btn variant="ghost" size="sm" icon="X" onClick={clearFilters}>Clear</Btn>
        )}
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--nx-danger-bg, #FEF2F2)', border: '1px solid var(--nx-danger-border, #FCA5A5)', borderRadius: 8, margin: '8px 0', color: 'var(--nx-danger, #DC2626)', fontSize: 13 }}>
          <NexusIcon name="AlertCircle" size={15} />
          <span>{error}</span>
          <Btn variant="ghost" size="sm" onClick={refresh} style={{ marginLeft: 'auto' }}>Retry</Btn>
        </div>
      )}

      <div className="nx-table-wrap">
        <div className="nx-table-scroll">
          <table className="nx-table">
            <thead>
              <tr>
                <Th icon="User">Actor</Th>
                <Th icon="Activity">Action</Th>
                <Th icon="Target">Target</Th>
                <Th icon="Layers">Category</Th>
                <Th icon="AlertCircle">Severity</Th>
                <Th icon="Clock">When</Th>
              </tr>
            </thead>
            <tbody>
              {renderTableBody()}
            </tbody>
          </table>
        </div>
      </div>

      {(hasMore || (loading && rows.length > 0)) && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
          <Btn variant="ghost" icon={loading ? 'Loader' : 'ChevronDown'} onClick={loadMore} disabled={loading}>
            {loading ? 'Loading…' : `Load more (${rows.length} of ${total.toLocaleString()})`}
          </Btn>
        </div>
      )}
    </div>
  );
}
